/**
 * Natural Phonics Page - Enhanced Version
 * Comprehensive phonics learning with games and progress tracking
 */
import { useState, useEffect } from 'react';
import {
  phonicsCategories,
  PhonicsRule,
  getAllPhonicsRules,
} from '../data/natural-phonics';
import { getWordEmoji } from '../data/phonics-images';
import { gameTypes, getUnlockConfig } from '../data/phonics-games';
import { phonicsProgressService, PhonicsBadge } from '../services/phonics-progress';
import { SoundToShapeGame, QuickJudgmentGame, PhonicsSpellingGame } from '../components/phonics/games';
import ExampleWordCard from '../components/phonics/ExampleWordCard';
import RuleProgressCard from '../components/phonics/RuleProgressCard';

type PronunciationStyle = 'american' | 'british';
type ViewMode = 'main' | 'adventure' | 'games' | 'game-play' | 'rules' | 'stats';
type GameType = 'sound-to-shape' | 'quick-judgment' | 'spelling';

const DEFAULT_USER_ID = 'default-user';

export default function NaturalPhonicsPage() {
  // View state
  const [view, setView] = useState<ViewMode>('main');
  const [pronunciationStyle, setPronunciationStyle] = useState<PronunciationStyle>('american');

  // Selection state
  const [selectedCategory, setSelectedCategory] = useState<string>(phonicsCategories[0].id);
  const [selectedRule, setSelectedRule] = useState<PhonicsRule | null>(null);
  const [selectedGame, setSelectedGame] = useState<GameType | null>(null);

  // Progress state
  const [totalRules, setTotalRules] = useState(0);
  const [overallProgress, setOverallProgress] = useState(0);
  const [newBadges, setNewBadges] = useState<PhonicsBadge[]>([]);

  // Calculate total rules
  useEffect(() => {
    const rules = getAllPhonicsRules();
    setTotalRules(rules.length);
  }, []);

  // Update progress
  useEffect(() => {
    setOverallProgress(phonicsProgressService.getOverallProgress(DEFAULT_USER_ID, totalRules));
  }, [totalRules, view]);

  // Check for new badges
  useEffect(() => {
    const badges = phonicsProgressService.checkAndAwardBadges(DEFAULT_USER_ID);
    if (badges.length > 0) {
      setNewBadges(badges);
    }
  }, [overallProgress]);

  // Handle game completion
  const handleGameComplete = (score: number, stars: number) => {
    if (selectedRule) {
      phonicsProgressService.updateRuleProgress(DEFAULT_USER_ID, selectedRule.id, {
        gameType: selectedGame === 'sound-to-shape' ? 'soundToShape' :
                  selectedGame === 'quick-judgment' ? 'quickJudgment' : 'spelling',
        score,
        correct: Math.round(stars * 2),
        total: 5,
        accuracy: stars * 33,
      });
    }
    setView('games');
  };

  // Render main menu
  const renderMainMenu = () => (
    <div className="space-y-6 animate-slide-up">
      {/* Header */}
      <div className="bg-gradient-to-r from-green-500 to-teal-500 rounded-3xl p-8 text-white shadow-xl">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold mb-2">📖 自然拼读</h1>
            <p className="text-white/80">
              学习英语发音规则，掌握英式和美式发音
            </p>
          </div>
          <div className="text-6xl">🎯</div>
        </div>

        {/* Progress bar */}
        <div className="bg-white/20 rounded-2xl p-4">
          <div className="flex justify-between text-sm mb-2">
            <span>学习进度</span>
            <span>{overallProgress}%</span>
          </div>
          <div className="h-3 bg-white/30 rounded-full overflow-hidden">
            <div
              className="h-full bg-white rounded-full transition-all duration-500"
              style={{ width: `${overallProgress}%` }}
            />
          </div>
        </div>

        {/* Style selector */}
        <div className="flex gap-3 mt-4">
          <button
            onClick={() => setPronunciationStyle('american')}
            className={`flex-1 py-3 rounded-xl font-bold transition-all ${
              pronunciationStyle === 'american'
                ? 'bg-blue-500 text-white'
                : 'bg-white/30 text-white'
            }`}
          >
            🇺🇸 美式
          </button>
          <button
            onClick={() => setPronunciationStyle('british')}
            className={`flex-1 py-3 rounded-xl font-bold transition-all ${
              pronunciationStyle === 'british'
                ? 'bg-red-500 text-white'
                : 'bg-white/30 text-white'
            }`}
          >
            🇬🇧 英式
          </button>
        </div>
      </div>

      {/* Main menu options */}
      <div className="grid grid-cols-2 gap-4">
        {[
          {
            id: 'adventure',
            icon: '🗺️',
            title: '规则闯关',
            description: '按顺序学习规则',
            color: 'from-green-400 to-teal-500',
            badge: '推荐',
          },
          {
            id: 'games',
            icon: '🎮',
            title: '游戏练习',
            description: '选择游戏模式',
            color: 'from-purple-400 to-pink-500',
          },
          {
            id: 'rules',
            icon: '📚',
            title: '规则浏览',
            description: '查看所有规则',
            color: 'from-blue-400 to-indigo-500',
          },
          {
            id: 'stats',
            icon: '📊',
            title: '学习统计',
            description: '进度和成就',
            color: 'from-orange-400 to-red-500',
          },
        ].map(item => (
          <button
            key={item.id}
            onClick={() => setView(item.id as ViewMode)}
            className={`bg-gradient-to-br ${item.color} rounded-2xl p-6 text-white text-left shadow-lg hover:shadow-xl transition-all hover:scale-105`}
          >
            <div className="flex items-start justify-between">
              <span className="text-4xl">{item.icon}</span>
              {item.badge && (
                <span className="bg-white/30 px-2 py-1 rounded-lg text-xs">
                  {item.badge}
                </span>
              )}
            </div>
            <h3 className="text-xl font-bold mt-4">{item.title}</h3>
            <p className="text-white/80 text-sm">{item.description}</p>
          </button>
        ))}
      </div>

      {/* New badges notification */}
      {newBadges.length > 0 && (
        <div className="bg-yellow-50 rounded-2xl p-6 border-2 border-yellow-300 animate-bounce-in">
          <h3 className="font-bold text-yellow-700 mb-3">🎉 恭喜获得新徽章！</h3>
          <div className="flex gap-4">
            {newBadges.map(badge => (
              <div key={badge.id} className="text-center animate-badge-pop">
                <span className="text-4xl">{badge.icon}</span>
                <p className="text-sm font-medium mt-1">{badge.name}</p>
              </div>
            ))}
          </div>
          <button
            onClick={() => setNewBadges([])}
            className="mt-4 w-full py-2 bg-yellow-200 text-yellow-800 rounded-xl font-medium"
          >
            知道了
          </button>
        </div>
      )}
    </div>
  );

  // Render adventure mode
  const renderAdventureMode = () => {
    const currentCategory = phonicsCategories.find(c => c.id === selectedCategory);

    return (
      <div className="space-y-6 animate-slide-up">
        {/* Back button */}
        <button
          onClick={() => setView('main')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-800"
        >
          <span className="text-2xl">←</span>
          <span>返回</span>
        </button>

        {/* Header */}
        <div className="bg-gradient-to-r from-green-500 to-teal-500 rounded-2xl p-6 text-white">
          <h2 className="text-2xl font-bold">🗺️ 规则闯关</h2>
          <p className="text-white/80 mt-1">按顺序解锁规则，逐步掌握</p>
        </div>

        {/* Category tabs */}
        <div className="flex flex-wrap gap-2">
          {phonicsCategories.map(cat => {
            const progress = phonicsProgressService.getCategoryProgress(DEFAULT_USER_ID, cat.id, cat.rules.length);
            const completedPercent = Math.round((progress.rulesCompleted / cat.rules.length) * 100);

            return (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id)}
                className={`px-4 py-2 rounded-xl font-medium transition-all ${
                  selectedCategory === cat.id
                    ? 'bg-primary text-white'
                    : 'bg-white text-gray-700 shadow-soft'
                }`}
              >
                <span className="mr-1">{cat.icon}</span>
                {cat.name}
                <span className="ml-2 text-xs opacity-70">{completedPercent}%</span>
              </button>
            );
          })}
        </div>

        {/* Rules list */}
        {currentCategory && (
          <div className="space-y-3">
            {currentCategory.rules.map((rule, index) => {
              const unlockConfig = getUnlockConfig(rule.id);
              const isUnlocked = phonicsProgressService.isRuleUnlocked(
                DEFAULT_USER_ID,
                rule.id,
                unlockConfig.prerequisiteRules,
                unlockConfig.minStars
              );
              const progress = phonicsProgressService.getProgress(DEFAULT_USER_ID);
              const ruleProgress = progress.ruleProgress[rule.id];

              return (
                <div key={rule.id} className="relative">
                  <RuleProgressCard
                    rule={rule}
                    progress={ruleProgress}
                    isUnlocked={isUnlocked}
                    isSelected={selectedRule?.id === rule.id}
                    onClick={() => {
                      if (isUnlocked) {
                        setSelectedRule(rule);
                        setView('games');
                      }
                    }}
                  />
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  // Render games selection
  const renderGamesSelection = () => (
    <div className="space-y-6 animate-slide-up">
      {/* Back button */}
      <button
        onClick={() => {
          setSelectedRule(null);
          setView('main');
        }}
        className="flex items-center gap-2 text-gray-600 hover:text-gray-800"
      >
        <span className="text-2xl">←</span>
        <span>返回</span>
      </button>

      {/* Selected rule info */}
      {selectedRule && (
        <div className="bg-gradient-to-r from-blue-500 to-purple-500 rounded-2xl p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold">{selectedRule.pattern}</h2>
              <p className="text-white/80">{selectedRule.description}</p>
            </div>
            <div className="text-4xl">🎯</div>
          </div>
        </div>
      )}

      {/* Game type selection */}
      <div className="space-y-4">
        <h3 className="font-bold text-gray-700">选择游戏模式</h3>

        {gameTypes.map(game => (
          <button
            key={game.id}
            onClick={() => {
              setSelectedGame(game.id as GameType);
              setView('game-play');
            }}
            className="w-full bg-white rounded-2xl p-5 shadow-soft text-left hover:shadow-lg transition-all hover:scale-[1.02]"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <span className="text-3xl">{game.icon}</span>
                <div>
                  <h4 className="font-bold text-gray-800">{game.name}</h4>
                  <p className="text-sm text-gray-500">{game.description}</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-500">{game.pointsPerQuestion}分/题</p>
                <p className="text-xs text-gray-400">{game.estimatedTime}</p>
              </div>
            </div>
          </button>
        ))}
      </div>

      {/* Quick start without rule selection */}
      {!selectedRule && (
        <div className="mt-8 p-4 bg-gray-50 rounded-xl text-center">
          <p className="text-gray-500 mb-3">选择一个规则开始学习</p>
          <button
            onClick={() => setView('adventure')}
            className="px-6 py-3 bg-primary text-white rounded-xl font-bold hover:shadow-lg transition-all"
          >
            选择规则 →
          </button>
        </div>
      )}
    </div>
  );

  // Render game play
  const renderGamePlay = () => {
    if (!selectedRule || !selectedGame) {
      setView('games');
      return null;
    }

    // Quick judgment needs multiple rules
    const rules = selectedGame === 'quick-judgment'
      ? phonicsCategories.find(c => c.id === selectedCategory)?.rules || [selectedRule]
      : [selectedRule];

    switch (selectedGame) {
      case 'sound-to-shape':
        return (
          <SoundToShapeGame
            rule={selectedRule}
            style={pronunciationStyle}
            onComplete={handleGameComplete}
            onExit={() => setView('games')}
          />
        );
      case 'quick-judgment':
        return (
          <QuickJudgmentGame
            rules={rules}
            style={pronunciationStyle}
            onComplete={handleGameComplete}
            onExit={() => setView('games')}
          />
        );
      case 'spelling':
        return (
          <PhonicsSpellingGame
            rule={selectedRule}
            style={pronunciationStyle}
            onComplete={handleGameComplete}
            onExit={() => setView('games')}
          />
        );
      default:
        return null;
    }
  };

  // Render rules browser
  const renderRulesBrowser = () => {
    const currentCategory = phonicsCategories.find(c => c.id === selectedCategory);

    return (
      <div className="space-y-6 animate-slide-up">
        {/* Back button */}
        <button
          onClick={() => setView('main')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-800"
        >
          <span className="text-2xl">←</span>
          <span>返回</span>
        </button>

        {/* Header */}
        <div className="bg-gradient-to-r from-blue-500 to-indigo-500 rounded-2xl p-6 text-white">
          <h2 className="text-2xl font-bold">📚 规则浏览</h2>
          <p className="text-white/80 mt-1">查看所有拼读规则</p>
        </div>

        {/* Category tabs */}
        <div className="flex flex-wrap gap-2">
          {phonicsCategories.map(cat => (
            <button
              key={cat.id}
              onClick={() => setSelectedCategory(cat.id)}
              className={`px-4 py-2 rounded-xl font-medium transition-all ${
                selectedCategory === cat.id
                  ? 'bg-primary text-white'
                  : 'bg-white text-gray-700 shadow-soft'
              }`}
            >
              <span className="mr-1">{cat.icon}</span>
              {cat.name}
            </button>
          ))}
        </div>

        {/* Category description */}
        {currentCategory && (
          <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl p-4">
            <div className="flex items-center gap-3">
              <span className="text-3xl">{currentCategory.icon}</span>
              <div>
                <h3 className="font-bold text-gray-800">{currentCategory.name}</h3>
                <p className="text-sm text-gray-600">{currentCategory.description}</p>
              </div>
            </div>
          </div>
        )}

        {/* Rules list with details */}
        {currentCategory && (
          <div className="space-y-4">
            {currentCategory.rules.map(rule => (
              <div key={rule.id} className="bg-white rounded-2xl shadow-soft overflow-hidden">
                {/* Rule header */}
                <div className="bg-gradient-to-r from-primary/10 to-purple-100 p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xl font-bold text-primary">{rule.pattern}</p>
                      <p className="text-sm text-gray-600">{rule.description}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-lg">🇺🇸</span>
                      <span className="font-mono">{rule.americanSound}</span>
                      {rule.britishSound && rule.britishSound !== rule.americanSound && (
                        <>
                          <span className="text-lg ml-2">🇬🇧</span>
                          <span className="font-mono">{rule.britishSound}</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {/* Examples */}
                <div className="p-4 space-y-2">
                  {rule.examples.map((example, idx) => (
                    <ExampleWordCard
                      key={idx}
                      example={example}
                      style={pronunciationStyle}
                      size="sm"
                      onPlay={() => {
                        const utterance = new SpeechSynthesisUtterance(example.word);
                        utterance.lang = pronunciationStyle === 'american' ? 'en-US' : 'en-GB';
                        utterance.rate = 0.7;
                        speechSynthesis.speak(utterance);
                      }}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  // Render stats
  const renderStats = () => {
    const progress = phonicsProgressService.getProgress(DEFAULT_USER_ID);
    const completedRules = Object.values(progress.ruleProgress).filter(
      r => r.status === 'completed' || r.status === 'mastered'
    ).length;

    return (
      <div className="space-y-6 animate-slide-up">
        {/* Back button */}
        <button
          onClick={() => setView('main')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-800"
        >
          <span className="text-2xl">←</span>
          <span>返回</span>
        </button>

        {/* Header */}
        <div className="bg-gradient-to-r from-orange-500 to-red-500 rounded-2xl p-6 text-white">
          <h2 className="text-2xl font-bold">📊 学习统计</h2>
          <p className="text-white/80 mt-1">你的学习进度和成就</p>
        </div>

        {/* Stats cards */}
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-white rounded-2xl p-5 shadow-soft text-center">
            <span className="text-4xl">⭐</span>
            <p className="text-3xl font-bold text-primary mt-2">{progress.totalStars}</p>
            <p className="text-sm text-gray-500">累计星星</p>
          </div>
          <div className="bg-white rounded-2xl p-5 shadow-soft text-center">
            <span className="text-4xl">🏆</span>
            <p className="text-3xl font-bold text-primary mt-2">{progress.totalPoints}</p>
            <p className="text-sm text-gray-500">总积分</p>
          </div>
          <div className="bg-white rounded-2xl p-5 shadow-soft text-center">
            <span className="text-4xl">📖</span>
            <p className="text-3xl font-bold text-primary mt-2">{completedRules}</p>
            <p className="text-sm text-gray-500">已学规则</p>
          </div>
          <div className="bg-white rounded-2xl p-5 shadow-soft text-center">
            <span className="text-4xl">🔥</span>
            <p className="text-3xl font-bold text-primary mt-2">{progress.currentStreak}</p>
            <p className="text-sm text-gray-500">连续天数</p>
          </div>
        </div>

        {/* Badges */}
        <div className="bg-white rounded-2xl p-6 shadow-soft">
          <h3 className="font-bold text-gray-800 mb-4">🏅 徽章收藏</h3>
          {progress.earnedBadges.length > 0 ? (
            <div className="grid grid-cols-4 gap-4">
              {progress.earnedBadges.map(badge => (
                <div key={badge.id} className="text-center">
                  <span className="text-3xl">{badge.icon}</span>
                  <p className="text-xs font-medium mt-1">{badge.name}</p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-4">
              还没有徽章，继续学习获得吧！
            </p>
          )}
        </div>

        {/* Reset button */}
        <button
          onClick={() => {
            if (confirm('确定要重置所有进度吗？')) {
              phonicsProgressService.resetProgress(DEFAULT_USER_ID);
              setView('main');
            }
          }}
          className="w-full py-3 bg-gray-100 text-gray-500 rounded-xl text-sm"
        >
          重置进度
        </button>
      </div>
    );
  };

  // Main render
  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white p-4">
      <div className="max-w-2xl mx-auto">
        {view === 'main' && renderMainMenu()}
        {view === 'adventure' && renderAdventureMode()}
        {view === 'games' && renderGamesSelection()}
        {view === 'game-play' && renderGamePlay()}
        {view === 'rules' && renderRulesBrowser()}
        {view === 'stats' && renderStats()}
      </div>
    </div>
  );
}
